import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
  getGetCollectionTicketClaimsQueryKey,
  getListCollectionTicketClaimMemberStatesQueryKey,
  getListTenantCollectionTicketClaimsQueryKey,
  useCreateTenantCollectionTicketClaims,
  useDeleteTenantCollectionTicketClaims,
  useUpdateTenantCollectionTicketClaims,
} from '@/api/trafikportalen';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { cn } from '@/helpers/classnames';
import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import Button from '@/components/core/Button';
import CheckBox from '@/components/core/Checkbox';
import { Dropdown } from '@/components/core/Dropdown';
import Input from '@/components/core/Input';
import { Loading } from '@/components/Loading';
import { NotificationDot } from '@/components/NotificationDot';
import { WarningBanner } from '@/components/core/WarningBanner';
import { getTenantConfiguration } from '@/constants/tenantConfiguration';
import {
  COLLECTION_TYPE_LABELS,
  type StudentCollectionType,
} from '@/helpers/collectionTypes';
import { organizationCanReceiveStudentTickets } from '@/helpers/organizationStatus';
import {
  claimsForPeriodEndEdit,
  groupCurrentPeriods,
  periodConfigurationKey,
} from '@/helpers/periodConfiguration';
import {
  getPeriodStatus,
  hasCurrentPeriod,
  periodStatusMessages,
} from '@/helpers/periodStatus';
import type { PeriodPanel } from '@/helpers/periodPageSearch';
import type { PeriodTagChanges } from '@/helpers/periodProducts';
import { useAllowPeriodProducts } from '@/hooks/useAllowPeriodProducts';
import { useOrganizationsMissingCurrentPeriod } from '@/hooks/useOrganizationsMissingCurrentPeriod';
import { useTenantID } from '@/hooks/useTenantID';
import { toast } from '@/hooks/useToast';
import { collator } from '@/utils/collator';
import { TenantCreatePeriodDialog } from './TenantCreatePeriodDialog';
import { TenantDeletePeriodDialog } from './TenantDeletePeriodDialog';
import { TenantEditPeriodDialog } from './TenantEditPeriodDialog';
import { TenantPeriodOrgRow } from './TenantPeriodOrgRow';
import { TenantUpcomingPeriodGroups } from './TenantUpcomingPeriodGroups';
import type { CreatePeriodFormData } from './CreatePeriodDialog';
import type { OptionsType } from '@/components/core/Dropdown';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';

const PERIOD_PANELS: PeriodPanel[] = ['organizations', 'periods'];

const PERIOD_COLUMNS_MIN_WIDTH = 960;

const useIsNarrowContainer = (ref: React.RefObject<HTMLElement | null>) => {
  const [isNarrow, setIsNarrow] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = (width: number) => {
      if (width <= 0) return;
      setIsNarrow(width < PERIOD_COLUMNS_MIN_WIDTH);
    };

    update(element.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      update(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return isNarrow;
};

const nextIndex = (current: number, length: number, key: string) => {
  if (key === 'ArrowRight') return (current + 1) % length;
  if (key === 'ArrowLeft') return (current - 1 + length) % length;
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return null;
};

const PERIOD_ORG_STATUS_FILTERS = [
  'all',
  'active',
  'upcoming',
  'none',
] as const;

type PeriodOrgStatusFilter = (typeof PERIOD_ORG_STATUS_FILTERS)[number];

const isPeriodOrgStatusFilter = (
  value: string,
): value is PeriodOrgStatusFilter =>
  (PERIOD_ORG_STATUS_FILTERS as readonly string[]).includes(value);

const matchesPeriodStatusFilter = (
  claims: CollectionTicketClaimDTO[],
  statusFilter: PeriodOrgStatusFilter,
) => {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'none') return !hasCurrentPeriod(claims);

  const currentStatuses = new Set(
    claims
      .map((claim) => getPeriodStatus(claim))
      .filter((status) => status !== 'past'),
  );
  return currentStatuses.has(statusFilter);
};

const PERIOD_ACCESS_FILTERS = [
  'all',
  'self-managed',
  'not-self-managed',
] as const;

type PeriodAccessFilter = (typeof PERIOD_ACCESS_FILTERS)[number];

const isPeriodAccessFilter = (value: string): value is PeriodAccessFilter =>
  (PERIOD_ACCESS_FILTERS as readonly string[]).includes(value);

const matchesPeriodAccessFilter = (
  organization: OrganizationDTO,
  accessFilter: PeriodAccessFilter,
) => {
  if (accessFilter === 'all') return true;
  const selfManaged = organization.selfmanaged_school_claims === true;
  return accessFilter === 'self-managed' ? selfManaged : !selfManaged;
};

const periodMatchesStatusFilter = (
  claim: CollectionTicketClaimDTO,
  statusFilter: PeriodOrgStatusFilter,
) => {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'none') return false;
  return getPeriodStatus(claim) === statusFilter;
};

interface TenantPeriodManagementProps {
  organizations: OrganizationDTO[];
  category?: StudentCollectionType;
  panel?: PeriodPanel;
  onCategoryChange?: (category: StudentCollectionType) => void;
  onPanelChange?: (panel: PeriodPanel) => void;
}

export const TenantPeriodManagement = ({
  organizations,
  category,
  panel: panelProp,
  onCategoryChange,
  onPanelChange,
}: TenantPeriodManagementProps) => {
  const { formatMessage } = useIntl();
  const tenantId = useTenantID();
  const queryClient = useQueryClient();
  const canEdit = checkUserPermissions({ permission: 'tenant:editor' });
  const { claimType, studentCategoryOrder: categories } =
    getTenantConfiguration(tenantId);

  const [uncontrolledCategory, setUncontrolledCategory] =
    useState<StudentCollectionType>(categories[0]);
  const [uncontrolledPanel, setUncontrolledPanel] =
    useState<PeriodPanel>('organizations');
  const collectionType = category ?? uncontrolledCategory;
  const panel = panelProp ?? uncontrolledPanel;
  const containerRef = useRef<HTMLDivElement>(null);
  const isNarrow = useIsNarrowContainer(containerRef);
  const categoryTabRefs = useRef<
    Partial<Record<StudentCollectionType, HTMLButtonElement | null>>
  >({});
  const panelTabRefs = useRef<
    Partial<Record<PeriodPanel, HTMLButtonElement | null>>
  >({});
  const panelsRef = useRef<HTMLDivElement>(null);
  const panelTablistRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<PeriodOrgStatusFilter>('all');
  const [accessFilter, setAccessFilter] = useState<PeriodAccessFilter>('all');
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editTargets, setEditTargets] = useState<
    CollectionTicketClaimDTO[] | null
  >(null);
  const [deleteTargets, setDeleteTargets] = useState<
    CollectionTicketClaimDTO[] | null
  >(null);
  const periodCoverage = useOrganizationsMissingCurrentPeriod(organizations);
  const claims = periodCoverage.claims[collectionType];
  const claimsLoading = periodCoverage.isLoading[collectionType];
  const claimsError = periodCoverage.isError[collectionType];
  const missingPeriodCount = periodCoverage.counts[collectionType];

  const createClaims = useCreateTenantCollectionTicketClaims();
  const allowPeriodProducts = useAllowPeriodProducts();
  const createInFlight = useRef(false);
  const updateClaims = useUpdateTenantCollectionTicketClaims();
  const deleteClaims = useDeleteTenantCollectionTicketClaims();

  const categoryOrganizations = useMemo(
    () =>
      organizations.filter(
        (organization) =>
          organizationCanReceiveStudentTickets(organization) &&
          (organization.enabled_collection_types ?? []).includes(
            collectionType,
          ),
      ),
    [organizations, collectionType],
  );

  const categoryOrgIds = useMemo(
    () => new Set(categoryOrganizations.map((organization) => organization.id)),
    [categoryOrganizations],
  );

  const categoryClaims = useMemo(
    () =>
      (claims ?? []).filter((claim) =>
        categoryOrgIds.has(claim.organization_id),
      ),
    [claims, categoryOrgIds],
  );

  const currentPeriodGroups = useMemo(
    () => groupCurrentPeriods(categoryClaims),
    [categoryClaims],
  );

  const visiblePeriodGroups = useMemo(() => {
    if (statusFilter === 'all') return currentPeriodGroups;
    return currentPeriodGroups.filter((group) => {
      const template = group.claims[0];
      return !!template && periodMatchesStatusFilter(template, statusFilter);
    });
  }, [currentPeriodGroups, statusFilter]);

  const claimsByOrg = useMemo(() => {
    const map = new Map<string, CollectionTicketClaimDTO[]>();
    for (const claim of categoryClaims) {
      const list = map.get(claim.organization_id) ?? [];
      list.push(claim);
      map.set(claim.organization_id, list);
    }
    return map;
  }, [categoryClaims]);

  const displayedClaimsByOrg = useMemo(() => {
    if (statusFilter === 'all') return claimsByOrg;
    const map = new Map<string, CollectionTicketClaimDTO[]>();
    for (const [organizationId, organizationClaims] of claimsByOrg) {
      map.set(
        organizationId,
        organizationClaims.filter((claim) =>
          periodMatchesStatusFilter(claim, statusFilter),
        ),
      );
    }
    return map;
  }, [claimsByOrg, statusFilter]);

  const organizationsById = useMemo(
    () =>
      new Map(
        organizations.map((organization) => [organization.id, organization]),
      ),
    [organizations],
  );

  const statusOptions = useMemo<OptionsType[]>(
    () => [
      {
        id: 'all',
        label: formatMessage({ defaultMessage: 'Alla' }),
      },
      {
        id: 'active',
        label: formatMessage(periodStatusMessages.active),
      },
      {
        id: 'upcoming',
        label: formatMessage(periodStatusMessages.upcoming),
      },
      {
        id: 'none',
        label: formatMessage({ defaultMessage: 'Inga aktuella perioder' }),
      },
    ],
    [formatMessage],
  );

  const selectedStatusOption = useMemo(
    () =>
      statusOptions.find((option) => option.id === statusFilter) ??
      statusOptions[0],
    [statusFilter, statusOptions],
  );

  const accessOptions = useMemo<OptionsType[]>(
    () => [
      {
        id: 'all',
        label: formatMessage({ defaultMessage: 'Alla' }),
      },
      {
        id: 'self-managed',
        label: formatMessage({ defaultMessage: 'Hanterar egna perioder' }),
      },
      {
        id: 'not-self-managed',
        label: formatMessage({
          defaultMessage: 'Hanterar inte egna perioder',
        }),
      },
    ],
    [formatMessage],
  );

  const selectedAccessOption = useMemo(
    () =>
      accessOptions.find((option) => option.id === accessFilter) ??
      accessOptions[0],
    [accessFilter, accessOptions],
  );

  const visibleOrganizations = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('sv');
    return [...categoryOrganizations]
      .filter((organization) => {
        if (
          needle &&
          !organization.name.toLocaleLowerCase('sv').includes(needle) &&
          !organization.organization_number
            .toLocaleLowerCase('sv')
            .includes(needle)
        ) {
          return false;
        }
        if (!matchesPeriodAccessFilter(organization, accessFilter)) {
          return false;
        }
        return matchesPeriodStatusFilter(
          claimsByOrg.get(organization.id) ?? [],
          statusFilter,
        );
      })
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [categoryOrganizations, claimsByOrg, query, statusFilter, accessFilter]);

  const selectedOrgIdList = useMemo(
    () => [...selectedOrgIds],
    [selectedOrgIds],
  );

  const selectedClaim = useMemo(
    () => categoryClaims.find((claim) => claim.id === selectedClaimId) ?? null,
    [categoryClaims, selectedClaimId],
  );

  const selectedPeriodKey = useMemo(() => {
    if (!selectedClaim || getPeriodStatus(selectedClaim) === 'past') {
      return null;
    }
    if (!periodMatchesStatusFilter(selectedClaim, statusFilter)) return null;
    return periodConfigurationKey(selectedClaim);
  }, [selectedClaim, statusFilter]);

  const selectedGroup = useMemo(
    () =>
      selectedPeriodKey
        ? (currentPeriodGroups.find(
            (group) => group.key === selectedPeriodKey,
          ) ?? null)
        : null,
    [currentPeriodGroups, selectedPeriodKey],
  );

  const highlightedOrgIds = useMemo(
    () => new Set(selectedGroup?.organizationIds ?? []),
    [selectedGroup],
  );

  const handleStatusFilterChange = useCallback((option: OptionsType) => {
    if (isPeriodOrgStatusFilter(option.id)) {
      setStatusFilter(option.id);
    }
  }, []);

  const handleAccessFilterChange = useCallback((option: OptionsType) => {
    if (isPeriodAccessFilter(option.id)) {
      setAccessFilter(option.id);
    }
  }, []);

  const scrollToPanelStart = useCallback(() => {
    const panels = panelsRef.current;
    const tablist = panelTablistRef.current;
    if (!panels || !tablist) return;
    const tablistStuck =
      tablist.getBoundingClientRect().top > panels.getBoundingClientRect().top;
    if (tablistStuck) panels.scrollIntoView?.({ block: 'start' });
  }, []);

  const updatePanel = useCallback(
    (next: PeriodPanel) => {
      if (next === panel) return;
      scrollToPanelStart();
      if (panelProp === undefined) setUncontrolledPanel(next);
      onPanelChange?.(next);
    },
    [onPanelChange, panel, panelProp, scrollToPanelStart],
  );

  const handleShowOrganizationsWithoutPeriod = useCallback(() => {
    setStatusFilter('none');
    if (isNarrow) updatePanel('organizations');
  }, [isNarrow, updatePanel]);

  const handleCategoryChange = useCallback(
    (type: StudentCollectionType) => {
      if (type === collectionType) return;
      if (category === undefined) setUncontrolledCategory(type);
      onCategoryChange?.(type);
      setSelectedOrgIds(new Set());
      setSelectedClaimId(null);
      setEditTargets(null);
      setDeleteTargets(null);
      setShowCreateDialog(false);
    },
    [category, collectionType, onCategoryChange],
  );

  const previousCategory = useRef(collectionType);
  useEffect(() => {
    if (previousCategory.current === collectionType) return;
    previousCategory.current = collectionType;
    setSelectedOrgIds(new Set());
    setSelectedClaimId(null);
    setEditTargets(null);
    setDeleteTargets(null);
    setShowCreateDialog(false);
  }, [collectionType]);

  const handleSelectPeriodOrganizations = useCallback(
    (organizationIds: string[]) => {
      setSelectedOrgIds(new Set(organizationIds));
      if (isNarrow) updatePanel('organizations');
    },
    [isNarrow, updatePanel],
  );

  const handleOrgSelectedChange = useCallback(
    (organizationId: string, selected: boolean) => {
      setSelectedOrgIds((prev) => {
        const next = new Set(prev);
        if (selected) next.add(organizationId);
        else next.delete(organizationId);
        return next;
      });
    },
    [],
  );

  const handlePeriodSelect = useCallback(
    (claim: CollectionTicketClaimDTO) => {
      const key = periodConfigurationKey(claim);
      setSelectedClaimId((currentId) => {
        if (!currentId) return claim.id;
        const current = categoryClaims.find((item) => item.id === currentId);
        if (current && periodConfigurationKey(current) === key) return null;
        return claim.id;
      });
      if (isNarrow) updatePanel('periods');
    },
    [categoryClaims, isNarrow, updatePanel],
  );

  const focusTab = useCallback(
    (element: HTMLButtonElement | null | undefined) => {
      element?.focus();
    },
    [],
  );

  const handleCategoryKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const index = nextIndex(
        categories.indexOf(collectionType),
        categories.length,
        event.key,
      );
      if (index === null) return;
      event.preventDefault();
      const next = categories[index];
      if (!next) return;
      handleCategoryChange(next);
      focusTab(categoryTabRefs.current[next]);
    },
    [categories, collectionType, focusTab, handleCategoryChange],
  );

  const handlePanelKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const index = nextIndex(
        PERIOD_PANELS.indexOf(panel),
        PERIOD_PANELS.length,
        event.key,
      );
      if (index === null) return;
      event.preventDefault();
      const next = PERIOD_PANELS[index];
      if (!next) return;
      updatePanel(next);
      focusTab(panelTabRefs.current[next]);
    },
    [focusTab, panel, updatePanel],
  );

  const handleEditPeriod = useCallback(
    (claims: CollectionTicketClaimDTO[]) => {
      setEditTargets(claimsForPeriodEndEdit(claims, selectedOrgIds));
    },
    [selectedOrgIds],
  );

  const handleRequestDeletePeriod = useCallback(
    (claims: CollectionTicketClaimDTO[]) => {
      const targets = claimsForPeriodEndEdit(claims, selectedOrgIds);
      if (
        targets.length === 0 ||
        targets.some((claim) => getPeriodStatus(claim) !== 'upcoming')
      ) {
        return;
      }
      setDeleteTargets(targets);
    },
    [selectedOrgIds],
  );

  const organizationCountForClaims = useCallback(
    (targets: CollectionTicketClaimDTO[] | null) => {
      if (!targets?.length) return 0;
      const key = periodConfigurationKey(targets[0]);
      const group = currentPeriodGroups.find((item) => item.key === key);
      if (group) return group.organizationIds.length;
      return new Set(targets.map((claim) => claim.organization_id)).size;
    },
    [currentPeriodGroups],
  );

  const editPeriodOrganizationCount = organizationCountForClaims(editTargets);
  const deletePeriodOrganizationCount =
    organizationCountForClaims(deleteTargets);

  const allVisibleSelected =
    visibleOrganizations.length > 0 &&
    visibleOrganizations.every((organization) =>
      selectedOrgIds.has(organization.id),
    );
  const someVisibleSelected = visibleOrganizations.some((organization) =>
    selectedOrgIds.has(organization.id),
  );

  const handleSelectAllChange = useCallback(
    (selected: boolean) => {
      const visibleIds = new Set(
        visibleOrganizations.map((organization) => organization.id),
      );
      setSelectedOrgIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleIds) {
          if (selected) next.add(id);
          else next.delete(id);
        }
        return next;
      });
    },
    [visibleOrganizations],
  );

  const invalidateClaims = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: getListTenantCollectionTicketClaimsQueryKey(),
    });
  }, [queryClient]);

  const handleCreate = useCallback(
    async (
      data: CreatePeriodFormData,
      organizationIds: string[],
      nonInvoiced: boolean,
      tags: PeriodTagChanges,
    ) => {
      if (createInFlight.current) return;
      createInFlight.current = true;
      try {
        await allowPeriodProducts(organizationIds, collectionType, {
          ...data,
          tags,
        });
        const created = await createClaims.mutateAsync({
          data: {
            collection_type: collectionType,
            organization_ids: organizationIds,
            data,
            non_invoiced: nonInvoiced,
          },
        });
        await invalidateClaims();
        setShowCreateDialog(false);
        toast.success(
          {
            title: formatMessage(
              {
                defaultMessage:
                  '{count, plural, one {# period skapades} other {# perioder skapades}}',
              },
              { count: created.length },
            ),
          },
          { autoClose: 3000 },
        );
      } catch {
        toast.error(
          {
            title: formatMessage({
              defaultMessage: 'Kunde inte skapa perioden',
            }),
          },
          { autoClose: 3000 },
        );
      } finally {
        createInFlight.current = false;
      }
    },
    [
      allowPeriodProducts,
      createClaims,
      collectionType,
      invalidateClaims,
      formatMessage,
    ],
  );

  const handleUpdatePeriod = useCallback(
    async (validTo: string, newValidFrom?: string) => {
      if (!editTargets?.length) return;
      try {
        const claimIdsByStart = new Map<string, string[]>();
        for (const claim of editTargets) {
          const validFrom = newValidFrom ?? claim.valid_from ?? '';
          const ids = claimIdsByStart.get(validFrom) ?? [];
          ids.push(claim.id);
          claimIdsByStart.set(validFrom, ids);
        }

        for (const [validFrom, claimIds] of claimIdsByStart) {
          await updateClaims.mutateAsync({
            data: {
              collection_ticket_claim_ids: claimIds,
              data: {
                ...(validFrom ? { valid_from: validFrom } : {}),
                valid_to: validTo,
              },
            },
          });
        }
        const endsImmediately =
          Math.abs(DateTime.fromISO(validTo).diffNow('seconds').seconds) < 30;
        if (endsImmediately) {
          const optimisticTo = DateTime.now()
            .minus({ milliseconds: 10 })
            .toISO({ suppressMilliseconds: true })!;
          const editedIds = new Set(editTargets.map((claim) => claim.id));
          queryClient.setQueriesData<CollectionTicketClaimDTO[]>(
            { queryKey: getListTenantCollectionTicketClaimsQueryKey() },
            (old) =>
              old?.map((claim) =>
                editedIds.has(claim.id)
                  ? { ...claim, valid_to: optimisticTo }
                  : claim,
              ),
          );
        }

        const collectionIds = [
          ...new Set(editTargets.map((claim) => claim.collection_id)),
        ];
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getListTenantCollectionTicketClaimsQueryKey(),
            refetchType: endsImmediately ? 'none' : 'active',
          }),
          ...editTargets.map((claim) =>
            queryClient.invalidateQueries({
              queryKey: getListCollectionTicketClaimMemberStatesQueryKey(
                claim.id,
              ),
            }),
          ),
          ...collectionIds.map((id) =>
            queryClient.invalidateQueries({
              queryKey: getGetCollectionTicketClaimsQueryKey(id),
            }),
          ),
        ]);
        setEditTargets(null);
        toast.success(
          {
            title: newValidFrom
              ? formatMessage({ defaultMessage: 'Perioden uppdaterades' })
              : formatMessage({ defaultMessage: 'Slutdatum uppdaterades' }),
          },
          { autoClose: 3000 },
        );
      } catch {
        toast.error(
          {
            title: newValidFrom
              ? formatMessage({
                  defaultMessage: 'Kunde inte uppdatera perioden',
                })
              : formatMessage({
                  defaultMessage: 'Kunde inte uppdatera slutdatum',
                }),
          },
          { autoClose: 3000 },
        );
      }
    },
    [updateClaims, editTargets, queryClient, formatMessage],
  );

  const handleDeletePeriods = useCallback(async () => {
    if (!deleteTargets?.length) return;
    try {
      await deleteClaims.mutateAsync({
        data: {
          collection_ticket_claim_ids: deleteTargets.map((claim) => claim.id),
        },
      });
      const collectionIds = [
        ...new Set(deleteTargets.map((claim) => claim.collection_id)),
      ];
      const deletedIds = new Set(deleteTargets.map((claim) => claim.id));
      queryClient.setQueriesData<CollectionTicketClaimDTO[]>(
        { queryKey: getListTenantCollectionTicketClaimsQueryKey() },
        (old) => old?.filter((claim) => !deletedIds.has(claim.id)),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getListTenantCollectionTicketClaimsQueryKey(),
        }),
        ...deleteTargets.map((claim) =>
          queryClient.invalidateQueries({
            queryKey: getListCollectionTicketClaimMemberStatesQueryKey(
              claim.id,
            ),
          }),
        ),
        ...collectionIds.map((id) =>
          queryClient.invalidateQueries({
            queryKey: getGetCollectionTicketClaimsQueryKey(id),
          }),
        ),
      ]);
      setSelectedClaimId(null);
      setEditTargets(null);
      setDeleteTargets(null);
      toast.success(
        {
          title: formatMessage(
            {
              defaultMessage:
                '{count, plural, one {Perioden raderades} other {# perioder raderades}}',
            },
            { count: deleteTargets.length },
          ),
        },
        { autoClose: 3000 },
      );
    } catch {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte radera perioden',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [deleteClaims, deleteTargets, formatMessage, queryClient]);

  return (
    <ViewPermissions permission="tenant:viewer">
      <div ref={containerRef} className="flex w-full grow flex-col gap-4 p-4">
        <div
          role="tablist"
          aria-label={formatMessage({ defaultMessage: 'Elevkategori' })}
          className="flex flex-wrap gap-1"
          onKeyDown={handleCategoryKeyDown}
        >
          {categories.map((type) => {
            const missingCount = periodCoverage.counts[type];
            const categoryLabel = formatMessage(COLLECTION_TYPE_LABELS[type]);
            const selected = collectionType === type;
            return (
              <Button
                key={type}
                id={`tenant-period-category-tab-${type}`}
                ref={(node) => {
                  categoryTabRefs.current[type] =
                    node instanceof HTMLButtonElement ? node : null;
                }}
                role="tab"
                variant={selected ? 'filled' : 'ghost'}
                aria-selected={selected}
                aria-controls="tenant-period-category-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => handleCategoryChange(type)}
              >
                {categoryLabel}
                {missingCount > 0 && (
                  <NotificationDot
                    count={missingCount}
                    label={formatMessage(
                      {
                        defaultMessage:
                          '{count, plural, one {# organisation utan period för {category}} other {# organisationer utan period för {category}}}',
                      },
                      { count: missingCount, category: categoryLabel },
                    )}
                  />
                )}
              </Button>
            );
          })}
        </div>
        <div
          id="tenant-period-category-panel"
          role="tabpanel"
          aria-labelledby={`tenant-period-category-tab-${collectionType}`}
          className="flex grow flex-col gap-4"
        >
          {missingPeriodCount > 0 && (
            <WarningBanner>
              <span className="flex flex-col items-start gap-3">
                <FormattedMessage
                  defaultMessage="{count, plural, one {# organisation med {category} saknar en aktiv eller kommande period.} other {# organisationer med {category} saknar en aktiv eller kommande period.}}"
                  values={{
                    count: missingPeriodCount,
                    category: formatMessage(
                      COLLECTION_TYPE_LABELS[collectionType],
                    ),
                  }}
                />
                <Button
                  variant="filled"
                  onClick={handleShowOrganizationsWithoutPeriod}
                >
                  <FormattedMessage defaultMessage="Visa organisationer utan period" />
                </Button>
              </span>
            </WarningBanner>
          )}

          <div className="flex flex-wrap items-end gap-3 pt-2">
            <div className="w-full max-w-sm">
              <Input
                id="tenant-period-search"
                type="text"
                label={formatMessage({ defaultMessage: 'Sök organisation' })}
                placeholder={formatMessage({
                  defaultMessage: 'Namn eller organisationsnummer',
                })}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className="mt-5 w-full max-w-xs">
              <Dropdown
                id="tenant-period-status-filter"
                label={formatMessage({ defaultMessage: 'Status' })}
                options={statusOptions}
                value={selectedStatusOption}
                preventReselect
                onValueChange={handleStatusFilterChange}
              />
            </div>
            <div className="mt-5 w-full max-w-xs">
              <Dropdown
                id="tenant-period-access-filter"
                label={formatMessage({ defaultMessage: 'Periodhantering' })}
                options={accessOptions}
                value={selectedAccessOption}
                preventReselect
                onValueChange={handleAccessFilterChange}
              />
            </div>
            {canEdit && (
              <Button
                variant="filled"
                disabled={selectedOrgIds.size === 0}
                onClick={() => setShowCreateDialog(true)}
              >
                <FormattedMessage defaultMessage="Skapa period" />
              </Button>
            )}
          </div>

          <p className="text-text-700" aria-live="polite" aria-atomic="true">
            <FormattedMessage
              defaultMessage="{count, plural, one {# organisation} other {# organisationer}}"
              values={{ count: visibleOrganizations.length }}
            />
            {canEdit && selectedOrgIds.size > 0 && (
              <>
                {', '}
                <FormattedMessage
                  defaultMessage="{count, plural, one {# markerad} other {# markerade}}"
                  values={{ count: selectedOrgIds.size }}
                />
              </>
            )}
          </p>

          {claimsLoading ? (
            <Loading />
          ) : claimsError ? (
            <p role="alert">
              <FormattedMessage defaultMessage="Kunde inte hämta perioderna." />
            </p>
          ) : (
            <div ref={panelsRef} className="flex grow flex-col gap-4">
              {isNarrow && (
                <div
                  ref={panelTablistRef}
                  role="tablist"
                  aria-label={formatMessage({
                    defaultMessage: 'Organisationer och perioder',
                  })}
                  className="sticky top-0 z-10 -mx-4 -my-2 flex flex-wrap gap-1 bg-background-50 px-4 py-2"
                  onKeyDown={handlePanelKeyDown}
                >
                  <Button
                    id="tenant-period-tab-organizations"
                    ref={(node) => {
                      panelTabRefs.current.organizations =
                        node instanceof HTMLButtonElement ? node : null;
                    }}
                    role="tab"
                    variant={panel === 'organizations' ? 'filled' : 'ghost'}
                    aria-selected={panel === 'organizations'}
                    aria-controls="tenant-period-panel-organizations"
                    tabIndex={panel === 'organizations' ? 0 : -1}
                    onClick={() => updatePanel('organizations')}
                  >
                    <FormattedMessage defaultMessage="Organisationer" />
                  </Button>
                  <Button
                    id="tenant-period-tab-periods"
                    ref={(node) => {
                      panelTabRefs.current.periods =
                        node instanceof HTMLButtonElement ? node : null;
                    }}
                    role="tab"
                    variant={panel === 'periods' ? 'filled' : 'ghost'}
                    aria-selected={panel === 'periods'}
                    aria-controls="tenant-period-panel-periods"
                    tabIndex={panel === 'periods' ? 0 : -1}
                    onClick={() => updatePanel('periods')}
                  >
                    <FormattedMessage defaultMessage="Perioder" />
                  </Button>
                </div>
              )}
              <div
                className={cn(
                  'flex grow gap-4',
                  isNarrow ? 'flex-col' : 'flex-row',
                )}
              >
                <div
                  id="tenant-period-panel-organizations"
                  role={isNarrow ? 'tabpanel' : undefined}
                  aria-labelledby={
                    isNarrow ? 'tenant-period-tab-organizations' : undefined
                  }
                  hidden={isNarrow && panel !== 'organizations'}
                  className={cn(
                    'flex min-w-0 grow-[2] basis-0 flex-col gap-2 pb-8',
                    isNarrow && panel !== 'organizations' && 'hidden',
                  )}
                >
                  {visibleOrganizations.length === 0 ? (
                    <p className="italic text-text-500">
                      <FormattedMessage defaultMessage="Inga organisationer att visa." />
                    </p>
                  ) : (
                    <>
                      {canEdit && (
                        <CheckBox
                          id="tenant-period-select-all"
                          label={formatMessage({
                            defaultMessage: 'Markera alla i listan',
                          })}
                          checked={
                            allVisibleSelected
                              ? true
                              : someVisibleSelected
                                ? 'indeterminate'
                                : false
                          }
                          onCheckedChange={(checked) =>
                            handleSelectAllChange(checked === true)
                          }
                        />
                      )}
                      <ul
                        aria-label={formatMessage({
                          defaultMessage: 'Organisationer',
                        })}
                        className="flex flex-col gap-2"
                      >
                        {visibleOrganizations.map((organization) => (
                          <TenantPeriodOrgRow
                            key={organization.id}
                            organization={organization}
                            claims={
                              displayedClaimsByOrg.get(organization.id) ?? []
                            }
                            canEdit={canEdit}
                            orgSelected={selectedOrgIds.has(organization.id)}
                            highlighted={highlightedOrgIds.has(organization.id)}
                            selectedPeriodKey={selectedPeriodKey}
                            onOrgSelectedChange={handleOrgSelectedChange}
                            onPeriodSelect={handlePeriodSelect}
                          />
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <aside
                  id="tenant-period-panel-periods"
                  role={isNarrow ? 'tabpanel' : undefined}
                  aria-label={
                    isNarrow
                      ? undefined
                      : formatMessage({ defaultMessage: 'Perioder' })
                  }
                  aria-labelledby={
                    isNarrow ? 'tenant-period-tab-periods' : undefined
                  }
                  hidden={isNarrow && panel !== 'periods'}
                  className={cn(
                    'min-w-0 grow basis-0',
                    !isNarrow && 'border-l-2 border-background-300 pl-4',
                    isNarrow && panel !== 'periods' && 'hidden',
                  )}
                >
                  <div
                    className={cn(
                      'pb-8',
                      !isNarrow &&
                        'sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto',
                    )}
                  >
                    <TenantUpcomingPeriodGroups
                      groups={visiblePeriodGroups}
                      emptyMessage={
                        statusFilter === 'all' ? undefined : (
                          <FormattedMessage defaultMessage="Inga perioder med vald status." />
                        )
                      }
                      organizationsById={organizationsById}
                      canEdit={canEdit}
                      selectedKey={selectedPeriodKey}
                      onSelect={handlePeriodSelect}
                      onSelectOrganizations={handleSelectPeriodOrganizations}
                      onEdit={handleEditPeriod}
                      onDelete={handleRequestDeletePeriod}
                    />
                  </div>
                </aside>
              </div>
            </div>
          )}
        </div>
      </div>

      {canEdit && (
        <>
          <TenantCreatePeriodDialog
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
            onSubmit={handleCreate}
            isSubmitting={createClaims.isPending}
            claimType={claimType}
            collectionType={collectionType}
            organizationIds={selectedOrgIdList}
            organizationsById={organizationsById}
            existingClaims={categoryClaims}
          />
          <TenantEditPeriodDialog
            open={!!editTargets?.length}
            claims={editTargets ?? []}
            periodOrganizationCount={editPeriodOrganizationCount}
            organizationsById={organizationsById}
            allClaims={categoryClaims}
            onClose={() => setEditTargets(null)}
            onSave={handleUpdatePeriod}
            isSaving={updateClaims.isPending}
          />
          <TenantDeletePeriodDialog
            open={!!deleteTargets?.length}
            claims={deleteTargets ?? []}
            periodOrganizationCount={deletePeriodOrganizationCount}
            organizationsById={organizationsById}
            onClose={() => setDeleteTargets(null)}
            onDelete={handleDeletePeriods}
            isDeleting={deleteClaims.isPending}
          />
        </>
      )}
    </ViewPermissions>
  );
};
